import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useEffect, useRef } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import {
  AsyncSelect,
  Button,
  FormCell,
  FormRow,
  FormSection,
  Multiselect,
  Select,
} from '~/components';
import { STATES } from '~/constants';
import { SubspecialtyFilter } from '~/features/market/listings/components/SubspecialtyFields';
import { useResetSubspecialties } from '~/features/market/listings/hooks/useSubspecialties';
import {
  EMPLOYMENT_CLASSIFICATION_OPTIONS,
  JOB_TYPE_OPTIONS,
} from '~/features/market/listings/job-listing/wizard-steps/basic-info/constants';
import { loadAOIOptionByProviderCategory } from '~/features/market/listings/msaas-listing/wizard-steps/basic-info/utils';
import {
  MIN_LOCATION_SEARCH_CHARS,
  searchGeographies,
} from '~/features/provider-hlx/service-listings/geographyApi';
import { MarketContentPanel } from '~/features/shared/market-content';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { useCustomForm } from '~/hooks/useCustomForm';
import {
  EMPTY_PROVIDER_JOB_SEARCH_FILTERS,
  providerJobSearchFilterSchema,
  ProviderJobSearchFilterValues,
  type ProviderJobSearchLocation,
  RADIUS_OPTIONS,
} from './providerJobSearchFilters.schema';

interface ProviderJobSearchFiltersProps {
  onSearch: (values: ProviderJobSearchFilterValues) => void;
  onClearAll: () => void;
  disabled?: boolean;
  /**
   * The provider's own category and specialty, once known (MCV-1822). They are the form's
   * starting values; Clear All still empties it.
   */
  initialValues?: ProviderJobSearchFilterValues | null;
}

const WORK_LOCATION_OPTIONS = [
  { value: 'ONSITE', label: 'Onsite' },
  { value: 'PARTIAL_REMOTE', label: 'Hybrid' },
  { value: 'REMOTE', label: 'Remote' },
];

// The picker waits for two characters (a ZIP or city prefix); a failed lookup shows no matches.
const loadLocationOptions = async (input: string): Promise<ProviderJobSearchLocation[]> => {
  if (input.trim().length < MIN_LOCATION_SEARCH_CHARS) return [];
  try {
    return (await searchGeographies(input)).map((geography) => ({
      value: String(geography.id),
      label: geography.label,
      latitude: Number(geography.latitude),
      longitude: Number(geography.longitude),
    }));
  } catch {
    return [];
  }
};

const ProviderJobSearchFiltersForm = ({
  onSearch,
  onClearAll,
  disabled,
}: ProviderJobSearchFiltersProps) => {
  const form = useFormContext<ProviderJobSearchFilterValues>();
  const { handleSubmit, reset, setValue, watch } = form;

  const providerCategory = watch('providerCategory');
  const providerSpecialty = watch('aoiSpecialty');
  const location = watch('location');
  const previousCategoryValueRef = useRef(providerCategory?.value);

  // The specialty option list is scoped to the selected category (see the
  // AsyncSelect's `key`), so a stale specialty selection from a different
  // category must be cleared whenever the category itself changes.
  useEffect(() => {
    if (previousCategoryValueRef.current !== providerCategory?.value) {
      previousCategoryValueRef.current = providerCategory?.value;
      setValue('aoiSpecialty', null);
    }
  }, [providerCategory?.value, setValue]);

  useResetSubspecialties(form);

  // Every filter is on screen, so an invalid submit has nothing to reveal: field errors show inline.
  const handleSearch = () => handleSubmit((values) => onSearch(values))();

  const handleClearAll = () => {
    reset(EMPTY_PROVIDER_JOB_SEARCH_FILTERS);
    onClearAll();
  };

  const FILTERS: { key: string; node: ReactNode }[] = [
    {
      key: 'providerCategory',
      node: (
        <AsyncSelect
          name="providerCategory"
          cyTag="provider-job-search-category"
          label="Provider Category"
          typography="comfortable"
          minChars={false}
          disabled={disabled}
          loadOptions={loadCategoryOptions}
        />
      ),
    },
    {
      key: 'aoiSpecialty',
      node: (
        <AsyncSelect
          name="aoiSpecialty"
          cyTag="provider-job-search-specialty"
          label="Provider Specialty"
          typography="comfortable"
          minChars={false}
          key={'aoiSpecialty-' + providerCategory?.value}
          disabled={disabled || !providerCategory?.value}
          loadOptions={(inputValue: string) => {
            if (providerCategory?.value) {
              return loadAOIOptionByProviderCategory(providerCategory.value, inputValue);
            }
            return Promise.resolve([]);
          }}
        />
      ),
    },
    ...(providerSpecialty?.value
      ? [
          {
            key: 'subspecialties',
            node: (
              <SubspecialtyFilter
                cyTag="provider-job-search-subspecialties"
                typography="comfortable"
                disabled={disabled}
              />
            ),
          },
        ]
      : []),
    {
      key: 'states',
      node: (
        <Multiselect
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          name="states"
          cyTag="provider-job-search-states"
          label="States"
          typography="comfortable"
          options={STATES}
          useShortValue
          disabled={disabled}
        />
      ),
    },
    {
      key: 'workLocation',
      node: (
        <Multiselect
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isClearable
          name="workLocation"
          cyTag="provider-job-search-work-location"
          label="Work Location"
          typography="comfortable"
          options={WORK_LOCATION_OPTIONS}
          disabled={disabled}
        />
      ),
    },
    {
      key: 'jobType',
      node: (
        <Multiselect
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isClearable
          name="jobType"
          cyTag="provider-job-search-job-type"
          label="Type"
          typography="comfortable"
          options={JOB_TYPE_OPTIONS}
          disabled={disabled}
        />
      ),
    },
    {
      key: 'employmentClassification',
      node: (
        <Multiselect
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isClearable
          name="employmentClassification"
          cyTag="provider-job-search-employment-type"
          label="Employment Type"
          typography="comfortable"
          options={EMPLOYMENT_CLASSIFICATION_OPTIONS}
          disabled={disabled}
        />
      ),
    },
    {
      key: 'location',
      node: (
        <AsyncSelect
          name="location"
          cyTag="provider-job-search-location"
          label="Location"
          typography="comfortable"
          placeholder="City, ST or ZIP"
          noOptionsMessage="Type a city or ZIP code"
          loadingMessage="Searching..."
          minChars={false}
          defaultOptions={false}
          filterOption={null}
          disabled={disabled}
          loadOptions={loadLocationOptions}
        />
      ),
    },
    {
      key: 'radiusMiles',
      node: (
        <Select
          name="radiusMiles"
          cyTag="provider-job-search-radius"
          label="Within"
          options={RADIUS_OPTIONS}
          disabled={disabled || !location}
        />
      ),
    },
  ];

  return (
    <MarketContentPanel variant="compact" dataCy="provider-job-search-filters">
      <FormSection componentsPerLine={4}>
        {FILTERS.map((filter) => (
          <FormRow key={'top-filter-bar-' + filter.key}>{filter.node}</FormRow>
        ))}
        <FormCell span={12}>
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            <button
              type="button"
              onClick={handleClearAll}
              data-cy="provider-job-search-clear-all-btn"
              className="text-sm font-medium text-primary-50 transition-colors hover:text-primary-t-300"
            >
              Clear All
            </button>
            <div className="shrink-0">
              <Button
                label="Search"
                cyTag="provider-job-search-search-btn"
                variant="rounded"
                onClick={handleSearch}
              />
            </div>
          </div>
        </FormCell>
      </FormSection>
    </MarketContentPanel>
  );
};

const SeededProviderJobSearchFilters = (props: ProviderJobSearchFiltersProps) => {
  const methods = useCustomForm<ProviderJobSearchFilterValues>({
    mode: 'onSubmit',
    resolver: zodResolver(providerJobSearchFilterSchema),
    defaultValues: props.initialValues ?? EMPTY_PROVIDER_JOB_SEARCH_FILTERS,
  });

  return (
    <FormProvider {...methods}>
      <ProviderJobSearchFiltersForm {...props} />
    </FormProvider>
  );
};

export const ProviderJobSearchFilters = (props: ProviderJobSearchFiltersProps) => (
  // The provider's defaults arrive after the first paint. The form is remounted with them as
  // its default values (rather than reset() into the mounted one) so the category-change
  // effect in the form starts from the seeded category and leaves the seeded specialty alone.
  <SeededProviderJobSearchFilters key={props.initialValues ? 'seeded' : 'empty'} {...props} />
);
